import dayjs, { Dayjs } from "dayjs";
import React from "react";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import checkFeatureActive from "./featureSwitch";
import { orderStatusMapping, PlanningStatus } from "@enums";
import { OrderStatus as OrderStatusType } from "@providers/hop-ord-server/api";
import { BYTES_IN_KB, BYTES_IN_MB } from "@constants";
import { useCheckUserRole } from "./useCheckUserRole";

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

// separator - comma: 7-Sept-2023, 1:26 PM
// separator - dot: 1:26PM • 7-Sept-2023
export const toLocaleDateTimeString = (
  datetime: Date | string | undefined | Dayjs,
  mode: "datetime" | "date" | "time" = "datetime",
  separator: "comma" | "dot" = "comma",
): string => {
  if (datetime === undefined || datetime === "") {
    throw new Error("Blank or undefined date");
  }

  datetime = dayjs(datetime).toDate();
  const parts = new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h12",
  })
    .formatToParts(new Date(datetime))
    .map((p) => p.value);

  const date = `${parts[0]}-${parts[2]}-${parts[4]}`;
  const time = `${parts[6]}:${parts[8]} ${parts[10].toUpperCase()}`;

  switch (mode) {
    case "datetime":
      return separator === "dot" ? `${time} • ${date}` : `${date}, ${time}`;
    case "date":
      return date;
    case "time":
      return time;
  }
};

export const getTimeZoneShort = (date: Date): string => {
  // https://github.com/iamkun/dayjs/issues/1154
  // dayjs has bug with .format(z) where it returns GMT + 10 instead of AEST
  return (
    dayjs(date)
      .format("zzz")
      .match(/\b(\w)/g)
      ?.join("") || ""
  );
};

export const dateOfBirthFormat = (dob: string): string => {
  return dayjs(dob).format("DD MMM YYYY");
};

export const toServerDateString = (date?: Date | string | Dayjs): string => {
  const datestr = dayjs(date).format("YYYY-MM-DD");
  if (datestr === "Invalid Date") {
    return "";
  }
  return datestr;
};

export const formatDurationRange = (
  start?: string | null,
  end?: string | null,
): string => {
  if (!start || !end) return "";
  const seconds = dayjs(end).diff(dayjs(start), "second");
  if (!Number.isFinite(seconds) || seconds < 0) return "";
  const days = Math.floor(seconds / 86400);
  const remAfterDays = seconds % 86400;
  const hours = Math.floor(remAfterDays / 3600);
  const remAfterHours = remAfterDays % 3600;
  const minutes = Math.floor(remAfterHours / 60);
  const remainingSeconds = remAfterHours % 60;

  if (days > 0) {
    return `${days}d ${String(hours).padStart(2, "0")}h`;
  }
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${String(remainingSeconds).padStart(2, "0")}s`;
  }
  return `${remainingSeconds}s`;
};

export interface ListDataOptionType {
  value: string;
  category: string;
}

export const mappedListData = (
  listData: ListDataOptionType[] | undefined,
  category: string,
) => {
  if (listData) {
    return listData
      .filter((option: ListDataOptionType) => option.category === category)
      .map((option: ListDataOptionType) => option.value);
  }
  return [];
};

export const isNumeric = (num: unknown) =>
  (typeof num === "number" || (typeof num === "string" && num.trim() !== "")) &&
  !isNaN(num as number);

export const areDoseValuesValid = (
  highDose: number | string | null,
  mid1Dose: number | string | null,
  mid2Dose: number | string | null,
  lowDose: number | string | null,
) => {
  const doses = [highDose, mid1Dose, mid2Dose, lowDose].filter(
    (dose) => dose !== null,
  );
  // high dose should always be set
  if (
    highDose === null ||
    !isNumeric(highDose) ||
    parseFloat(highDose.toString()) <= 0
  )
    return false;
  const nonNullDoses: (string | number)[] = doses.filter(
    (dose) => dose !== null,
  ) as (string | number)[];
  // if the doses are not numeric then return false
  if (
    nonNullDoses.some(
      (dose) =>
        !isNumeric(dose) || dose === "" || parseFloat(dose.toString()) <= 0,
    )
  )
    return false;
  // if the doses are not in ascending order then return false
  if (
    nonNullDoses.some(
      (dose, index) =>
        index > 0 &&
        parseFloat(dose.toString()) >=
          parseFloat(nonNullDoses[index - 1].toString()),
    )
  )
    return false;
  return true;
};

export const areDosesInOrder = (doses: (string | number)[]) => {
  const numberDoses = doses.map((dose) => parseFloat(dose.toString()));
  return numberDoses.map(
    (dose, index) =>
      index === 0 ||
      !numberDoses.some((dose2, index2) => index2 < index && dose >= dose2),
  );
};

export const isDoseArrayValid = (doses: (string | number | null)[]) => {
  type doseDictType = {
    highDose: number | string | null;
    mid1Dose: number | string | null;
    mid2Dose: number | string | null;
    lowDose: number | string | null;
  };
  const doseDict: doseDictType = {
    highDose: null,
    mid1Dose: null,
    mid2Dose: null,
    lowDose: null,
  };
  const doseMapping = [
    [],
    ["highDose"],
    ["highDose", "lowDose"],
    ["highDose", "mid1Dose", "lowDose"],
    ["highDose", "mid1Dose", "mid2Dose", "lowDose"],
  ];
  if (!Array.isArray(doses)) return false;
  doseMapping[doses.length].forEach((dose, index) => {
    doseDict[dose as keyof doseDictType] = doses[index];
  });
  return areDoseValuesValid(
    doseDict.highDose,
    doseDict.mid1Dose,
    doseDict.mid2Dose,
    doseDict.lowDose,
  );
};

export const useInterval = (callback: () => void, delay: number | null) => {
  const intervalRef = React.useRef<number | null>(null);
  const savedCallback = React.useRef(callback);
  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  React.useEffect(() => {
    const tick = () => savedCallback.current();
    if (typeof delay === "number") {
      intervalRef.current = window.setInterval(tick, delay);
      return () => window.clearInterval(intervalRef.current || 0);
    }
  }, [delay]);
  return intervalRef;
};

export const isSpecifyMandatory = (siteName: string | undefined) =>
  siteName?.toLocaleLowerCase().includes("specify") || false;

export type OptionData = {
  value: string;
  category: string;
};

export const parseListData = (
  optionData: OptionData[],
  categories: string[],
): { [category: string]: string[] } => {
  const options = Object.fromEntries(
    categories.map((c) => [c, [] as string[]]),
  );
  if (optionData === undefined) {
    return options;
  }
  for (const ldo of optionData) {
    options[ldo.category]?.push(ldo.value);
  }
  return options;
};

export const timeFromNow = (
  createdAt: string,
  mode: "datetime" | "date" = "datetime",
) => {
  // If time is today, show relative time (e.g. 3 mins ago).
  // Otherwise show exact time and date (e.g. 3:02PM • 27-May-2023)
  if (!createdAt) return "";

  const NOW_THRESHOLD_SECS = 6;
  const today = new Date();
  const timesince = today.getTime() - new Date(createdAt).getTime();
  const secondsSince = Math.floor(timesince / 1000);
  const minutesSince = Math.floor(secondsSince / 60);
  const hoursSince = Math.floor(minutesSince / 60);

  function pluralise(val: number, suffix: string): string {
    const plural = val > 1 ? `${val} ${suffix}s` : `${val} ${suffix}`;
    return `${plural} ago`;
  }

  if (
    toLocaleDateTimeString(createdAt, "date") !==
    toLocaleDateTimeString(today, "date")
  ) {
    return toLocaleDateTimeString(
      createdAt,
      mode === "date" ? "date" : "datetime",
    );
  }

  if (hoursSince > 0) {
    return pluralise(hoursSince, "hr");
  }
  if (minutesSince > 0) {
    return pluralise(minutesSince, "min");
  }
  if (secondsSince > NOW_THRESHOLD_SECS) {
    return pluralise(secondsSince, "sec");
  }
  return "Now";
};

export const getAvatarText = (name: string | undefined): string | undefined =>
  name &&
  name
    .toUpperCase()
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(-2);

export { checkFeatureActive };

export const getNiceOrderStatus = (
  status: OrderStatusType | undefined,
): string => {
  if (!status) return "";

  // Check if the status exists in the mapping
  if (status in orderStatusMapping) {
    return orderStatusMapping[status];
  }

  // Fallback: split by underscore and convert to title case
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const getPlanningStatus = (
  status: OrderStatusType | undefined,
  ctExists: boolean,
  rtStructExists: boolean,
  hasHandoverDatetime: boolean,
): PlanningStatus | null => {
  if (!status) return null;

  switch (status) {
    case "draft":
      return PlanningStatus.DRAFT;
    case "submitted":
      if (ctExists && rtStructExists) return PlanningStatus.READY_FOR_PLANNING;
      return PlanningStatus.NEW_CAREPLAN;
    case "sent_to_automation":
      return PlanningStatus.PLANNING;
    case "ready_for_ro_approval":
      return PlanningStatus.PENDING_PLAN_REVIEW;
    case "approved":
      return PlanningStatus.APPROVED;
    case "rejected":
      return PlanningStatus.REJECTED;
    default:
      if (hasHandoverDatetime) return PlanningStatus.HANDOVER_COMPLETE;
      return null;
  }
};

export const formatSize = (size: number): string => {
  if (size === 0) return "0 Bytes";
  if (size < BYTES_IN_KB) return "<1 KB";
  if (size < BYTES_IN_MB) return `${(size / BYTES_IN_KB).toFixed(1)} KB`;
  return `${(size / BYTES_IN_MB).toFixed(1)} MB`;
};

// typesafe-er countBy that returns the same shape as the init object
export const countBy = <T, AllK extends string, K extends AllK>(
  arr: T[],
  keyGetter: (item: T) => AllK,
  init: { [key in K]: number },
): typeof init => {
  return arr.reduce(
    (acc: typeof init, item): typeof init => {
      const key = keyGetter(item);
      if (Object.hasOwn(acc, key)) {
        acc[key as K] += 1;
      }
      return acc;
    },
    { ...init },
  );
};

export { useCheckUserRole };
